import React, { createContext, ReactNode, useContext, useMemo } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import keyBy from "lodash/keyBy";
import differenceBy from "lodash/differenceBy";
import { Dictionary } from "lodash";

import useBurstQuery from "modules/hooks/use-burst-query";
import {
  DistributionGroupFragment,
  DistributionGroupQuery,
  DistributionGroupQueryVariables,
  DistributionGroupListQuery,
} from "gql/graphql";
import DISTRIBUTION_GROUP_QUERY from "./networking/distribution-group";
import DISTRIBUTION_GROUP_LIST_QUERY from "./networking/distribution-group-list";
import { useAuth } from "../auth-provider";

interface IContextProps {
  distributionGroups: readonly DistributionGroupFragment[];
  activeDistributionGroups: readonly DistributionGroupFragment[];
  distributionGroupsById: Dictionary<DistributionGroupFragment | undefined>;
  getDistributionGroupsById(ids: string[]): DistributionGroupFragment[];
  refreshDistributionGroups: (id: string[]) => void;
}

const DistributionGroupsContext = createContext<IContextProps>({
  distributionGroups: [],
  activeDistributionGroups: [],
  distributionGroupsById: {},
  getDistributionGroupsById: () => [],
  refreshDistributionGroups: () => {},
});

export interface IProviderProps {
  children?: ReactNode;
}

export const DistributionGroupsProvider = ({ children }: IProviderProps) => {
  const { cache } = useApolloClient();
  const { isLoggedIn } = useAuth();

  const [distributionGroupAPI] = useBurstQuery<
    DistributionGroupQuery,
    DistributionGroupQueryVariables
  >(DISTRIBUTION_GROUP_QUERY);

  const { data } = useQuery<DistributionGroupListQuery>(
    DISTRIBUTION_GROUP_LIST_QUERY,
    {
      skip: !isLoggedIn,
    },
  );

  const distributionGroups = useMemo(
    () => data?.distributionGroupList || [],
    [data],
  );

  const activeDistributionGroups = useMemo(
    () => distributionGroups.filter((group) => group.active),
    [distributionGroups],
  );

  const distributionGroupsById = useMemo(
    () => keyBy(distributionGroups || [], "id"),
    [distributionGroups],
  );

  const value = useMemo<IContextProps>(
    () => ({
      distributionGroups,
      activeDistributionGroups,
      distributionGroupsById,
      getDistributionGroupsById: (ids) =>
        ids
          .map((id) => distributionGroupsById[id])
          .filter((distributionGroup) => !!distributionGroup), //filter ensures that the lookup is not undefined
      refreshDistributionGroups: (idList) => {
        distributionGroupAPI({ variables: { id: idList } })
          .then((result) => {
            const distributionGroups = result.data?.distributionGroups;
            if (!distributionGroups) {
              return;
            }

            cache.updateQuery<DistributionGroupListQuery>(
              {
                query: DISTRIBUTION_GROUP_LIST_QUERY,
              },
              (data) => {
                if (!data) {
                  return data;
                }

                const _distributionGroups = data.distributionGroupList;

                // Remove any/all distribution groups that were called for in the query but weren't returned; these have been deleted.
                const filteredDistributionGroups = _distributionGroups.filter(
                  (distributionGroup) => !idList.includes(distributionGroup.id),
                );

                // Ensure new distribution groups that were just created are added to the AllDistributionGroupsQuery.
                const newDistributionGroups = differenceBy(
                  distributionGroups,
                  filteredDistributionGroups,
                  "id",
                );

                return {
                  ...data,
                  distributionGroupList: [
                    ...filteredDistributionGroups,
                    ...newDistributionGroups,
                  ],
                };
              },
            );
          })
          .catch(() => {
            // best-effort refresh: on a failed fetch the cached list stays as-is
          });
      },
    }),
    [
      activeDistributionGroups,
      cache,
      distributionGroupAPI,
      distributionGroups,
      distributionGroupsById,
    ],
  );

  return (
    <DistributionGroupsContext.Provider value={value}>
      {children}
    </DistributionGroupsContext.Provider>
  );
};

export const useDistributionGroups = () =>
  useContext(DistributionGroupsContext);
